/**
 * WebAuthn platform authenticator support detection.
 *
 * Checks whether the current browser supports:
 *   1. The Credential Management API (navigator.credentials)
 *   2. The PublicKeyCredential interface
 *   3. A user-verifying platform authenticator (Face ID, fingerprint,
 *      Windows Hello) — as opposed to a roaming FIDO key.
 *
 * The detection is async because isUserVerifyingPlatformAuthenticatorAvailable()
 * returns a Promise. Results are cached after the first call.
 */

let cachedSupport: boolean | null = null

/**
 * Returns true if the browser supports WebAuthn with a platform
 * authenticator (biometric). Caches the result after the first call.
 */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (cachedSupport !== null) return cachedSupport

  if (
    typeof window === 'undefined' ||
    typeof navigator === 'undefined' ||
    !navigator.credentials ||
    !window.PublicKeyCredential
  ) {
    cachedSupport = false
    return false
  }

  const PKC = window.PublicKeyCredential
  if (typeof PKC.isUserVerifyingPlatformAuthenticatorAvailable !== 'function') {
    cachedSupport = false
    return false
  }

  try {
    cachedSupport = await PKC.isUserVerifyingPlatformAuthenticatorAvailable()
  } catch {
    cachedSupport = false
  }

  return cachedSupport
}

/**
 * Synchronous check: does the browser have the Credential Management
 * and PublicKeyCredential APIs at all? Does not confirm a platform
 * authenticator is present — use isPlatformAuthenticatorAvailable for that.
 */
export function hasWebAuthnApi(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.credentials &&
    !!window.PublicKeyCredential
  )
}

/**
 * Resets the cached support detection. Exported for tests only.
 */
export function resetSupportCache(): void {
  cachedSupport = null
}
